//
// See: https://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
//
// W3C Web Content Accessibility Guidelines (WCAG) 2.0 defines relative luminance as the relative
// brightness of any point in a colorspace, normalized to 0 for darkest black and 1 for lightest
// white.
//
// For the sRGB colorspace, the relative luminance of a color is defined as
//     L = 0.2126 * R + 0.7152 * G + 0.0722 * B where R, G and B are defined as:
//
// Where:
//     if RsRGB <= 0.03928 then R = RsRGB/12.92 else R = ((RsRGB+0.055)/1.055) ^ 2.4
//     if GsRGB <= 0.03928 then G = GsRGB/12.92 else G = ((GsRGB+0.055)/1.055) ^ 2.4
//     if BsRGB <= 0.03928 then B = BsRGB/12.92 else B = ((BsRGB+0.055)/1.055) ^ 2.4
//
// And RsRGB, GsRGB, and BsRGB are defined as:
//     RsRGB = R8bit/255
//     GsRGB = G8bit/255
//     BsRGB = B8bit/255
//
const RED = 0.2126;   // Taken from W3C Web Content Accessibility Guidelines (WCAG) 2.0 (SMPTE C, Rec. 709 color channel weights)
const GREEN = 0.7152; // Taken from W3C Web Content Accessibility Guidelines (WCAG) 2.0 (SMPTE C, Rec. 709 color channel weights)
const BLUE = 0.0722;  // Taken from W3C Web Content Accessibility Guidelines (WCAG) 2.0 (SMPTE C, Rec. 709 color channel weights)
const GAMMA = 2.4;    // Taken from W3C Web Content Accessibility Guidelines (WCAG) 2.0

/**
 * Calculate a number representing the luminance or "brightness" of a color. This is used to to
 * calculate contrast between text and background.
 * See: https://stackoverflow.com/a/9733420
 * @param r
 * @param g
 * @param b
 * @returns
 */
export function luminance(r: number, g: number, b: number) {
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928
      ? v / 12.92
      : Math.pow((v + 0.055) / 1.055, GAMMA);
  });
  return a[0] * RED + a[1] * GREEN + a[2] * BLUE;
}

/**
 * Calculate the contrast ratio between two colors.
 * @param r1
 * @param g1
 * @param b1
 * @param r2
 * @param g2
 * @param b2
 * @returns
 */
export function contrast(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number) {
  const lum1 = luminance(r1, g1, b1);
  const lum2 = luminance(r2, g2, b2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}

/**
 * See: https://css-tricks.com/converting-color-spaces-in-javascript/
 * @param r
 * @param g
 * @param b
 * @returns
 */
export function RGBToHSL(r: number, g: number, b: number) {
  // Make r, g, and b fractions of 1
  r /= 255;
  g /= 255;
  b /= 255;

  // Find greatest and smallest channel values
  const cmin = Math.min(r,g,b),
    cmax = Math.max(r,g,b),
    delta = cmax - cmin;

  let h = 0,
    s = 0,
    l = 0;

  // Calculate hue
  // No difference
  if (delta == 0)
    h = 0;
  // Red is max
  else if (cmax == r)
    h = ((g - b) / delta) % 6;
  // Green is max
  else if (cmax == g)
    h = (b - r) / delta + 2;
  // Blue is max
  else
    h = (r - g) / delta + 4;

  h = Math.round(h * 60);

  // Make negative hues positive behind 360°
  if (h < 0) {
    h += 360;
  }

  // Calculate lightness
  l = (cmax + cmin) / 2;

  // Calculate saturation
  s = delta == 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  // Multiply l and s by 100
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);

  return [h, s, l];
}
